import React, { useEffect, useState, useRef } from 'react';
import { Table, Accordion } from 'react-bootstrap';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';
import { usePageTableData } from '../../../hooks/usePageTableData';
import Field from '../Wizard/Field/Field';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { general } from '../../../general';

import ExcelJS from 'exceljs';

import styles from './PageTable.module.scss';

function PageTable(props: any) {
	const showLabels = props.options.showFields;
	const auth = useAuth();
	const axiosHandler = useAxios();
	const axios = axiosHandler.axios;
	const t = useTranslate();

	// View mode - controlled by parent component via props, defaults to 'list'
	const viewMode: 'list' | 'card' | 'map' = props.viewMode || 'list';
	// Map location field - field to use for geocoding (e.g., 'POSTAL_CODE')
	const mapLocationField: string = props.mapLocationField || 'POSTAL_CODE';

	const [updateData, setUpdateData] = useState<any>("initial");
	const [permaFiltersPrevious, setPermaFiltersPrevious] = useState<any>(null);

	// Sidetallet lagres sammen med filtrene, under en nøkkel avledet av
	// saveFiltersKey. Da følger det samme inndeling per side og rase, og man
	// kommer tilbake til samme side etter å ha klikket inn på en rad og trykket
	// tilbake. Uten saveFiltersKey oppfører tabellen seg som før.
	const savedPageKey = props.saveFiltersKey != undefined ? props.saveFiltersKey + ".page" : undefined;

	function readSavedPage(key: string | undefined) {
		if (key == undefined) return 0;
		let saved = parseInt(localStorage.getItem(key) || "");
		return isNaN(saved) || saved < 0 ? 0 : saved;
	}

	const [ currentPageNumber, setCurrentPageState ] = useState(() => readSavedPage(savedPageKey));

	// Alle kallestedene sender et tall, så lagringen kan gjøres her framfor i en
	// egen effekt — da slipper vi at en effekt rekker å skrive gammelt sidetall
	// under en ny nøkkel når rasen byttes.
	const setCurrentPage = (value: number) => {
		setCurrentPageState(value);
		if (savedPageKey != undefined) {
			localStorage.setItem(savedPageKey, String(value));
		}
	};

	const isFirstPermaFilterRun = useRef(true);

	// Filterfeltene er ukontrollerte (defaultValue), så de oppdaterer seg ikke av
	// seg selv når vi laster inn lagrede filtre for en ny nøkkel. Bytter man rase
	// uten at tabellen remountes, ville boksene ellers stå igjen med forrige rases
	// tekst mens spørringen kjørte på den nye. Å bumpe dette tallet remounter
	// inputene, og skjer bare ved nøkkelbytte — ikke mens man skriver.
	const [ filterInputGeneration, setFilterInputGeneration ] = useState(0);

	const [ pageSize, setPageSize ] = useState(props.options != undefined && props.options.pageSize != undefined ? props.options.pageSize : 15);
	const [ selectedOption, setSelectedOption ] = useState(0);
	const [ dataUrl ] = useState(props.dataUrl);
	
    const [ searchValueArray, setSearchValueArray ] = useState<{
		field: string;
		type: string;
		values: string[];
		exact: boolean;
	}[]>(props.filters != undefined ? props.filters : props.options.showFields.map((label: any) => {
		return { field: label?.field, type: "and", values: [''], exact: false };
	}));
    const [ selectedHeaderField, setSelectedHeaderField ] = useState(props.defaultSortField != undefined ? props.defaultSortField : "_id" ); 
    const [ sortDirection, setSortDirection ] = useState(props.defaultSortDirection != undefined ? props.defaultSortDirection : "desc" );

	// Build selectedCols for React Query
	const selectedCols = [
		...(props.permaFilters != undefined ? props.permaFilters.map((filter: any) => filter.field) : []),
		...(props.selectedTraits != undefined ? props.selectedTraits : []),
		...(showLabels != undefined ? showLabels.map((label: any) => label?.field?.split("|")[0]) : []),
		props.showOnlyBreedingdogs == true ? "BREEDING_ALLOWED" : "",
		props.showOnlyApproveddogs == true ? "APPROVEDDOGS_ONLY" : "",
		props.includeDogsOnWait == true ? "DOGS_ON_WAIT_ONLY" : "",
	].filter((value: any, index, self) => value != null && value !== "" && !props?.excludedFields?.includes(value) && self.indexOf(value) === index);

	// Build filters for React Query
	const filters = [
		...(props.permaFilters != undefined ? props.permaFilters : []),
		...(searchValueArray ?? []).filter((searchValue: any) => searchValue['values'].length != 0),
	];

	// Use React Query hook
	const { data: queryData, isLoading, isFetching, refetch } = usePageTableData({
		dataUrl,
		currentPageNumber,
		pageSize,
		sortField: selectedHeaderField,
		sortDirection,
		selectedCols,
		filters,
		permaFilters: props.permaFilters,
		exclusiveFilters: props.exclusiveFilters,
		additionalData: props.additionalData,
		data: props.data,
		enabled: true,
	});

	const data = queryData?.pagedata || [{}];
	const pageData = queryData || {};
	const loading = isLoading;
	const softLoading = isFetching && !isLoading;

	const showItemsRange = [ 3, 5, 10, 15, 25, 50, 100 ];

	// Rader nøkles på posisjon med mindre forelderen oppgir en stabil nøkkel. Med
	// posisjonsnøkler gjenbruker React radene når dataene byttes ut (fane, side,
	// sortering, hurtigbuffer), og ukontrollerte inputer i en override beholder
	// da forrige rads verdi. Tabeller med redigerbare celler må sende rowKey.
	function rowKeyFor(dataLine: any, index: number) {
		return props.rowKey != undefined ? props.rowKey(dataLine) ?? index : index;
	}

	// Map view state
	const mapRef = useRef<L.Map>(null);
	const [geocodingCache, setGeocodingCache] = useState<{ [key: string]: [number, number] }>({});
	const [geocodingInProgress, setGeocodingInProgress] = useState<Set<string>>(new Set());
	const [popupStates, setPopupStates] = useState<{ [key: string]: { currentPage: number; totalPages: number } }>({});
	const [dataWithLocation, setDataWithLocation] = useState<any[]>([]);
	const [dataByLocation, setDataByLocation] = useState<{ [key: string]: any[] }>({});
	const [isGeocodingComplete, setIsGeocodingComplete] = useState(false);

	// Fix for default markers in react-leaflet
	useEffect(() => {
		if (typeof window !== 'undefined') {
			delete (L.Icon.Default.prototype as any)._getIconUrl;
			L.Icon.Default.mergeOptions({
				iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
				iconUrl: require('leaflet/dist/images/marker-icon.png'),
				shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
			});
		}
	}, []);

	// Geocoding function with rate limiting and retry logic
	const geocodeLocation = async (locationValue: string, retryCount = 0): Promise<[number, number] | null> => {
		if (geocodingCache[locationValue]) {
			return geocodingCache[locationValue];
		}

		if (geocodingInProgress.has(locationValue)) {
			return null;
		}

		try {
			setGeocodingInProgress(prev => new Set(prev).add(locationValue));
			
			const url = `https://nominatim.openstreetmap.org/search?postalcode=${locationValue}&country=Norge&format=json&limit=1`;
			
			const response = await fetch(url);
			
			if (!response.ok) {
				throw new Error(`Geocoding request failed with status: ${response.status}`);
			}
			
			const apiData = await response.json();
			
			if (apiData && apiData.length > 0) {
				const coordinates: [number, number] = [parseFloat(apiData[0].lat), parseFloat(apiData[0].lon)];
				
				setGeocodingCache(prev => ({
					...prev,
					[locationValue]: coordinates
				}));
				
				return coordinates;
			}
			
			return null;
		} catch (error) {
			// Retry on HTTP/2 errors or network issues
			if (retryCount < 3) {
				const delay = (retryCount + 1) * 500; // Exponential backoff: 500ms, 1000ms, 1500ms
				await new Promise(resolve => setTimeout(resolve, delay));
				
				setGeocodingInProgress(prev => {
					const newSet = new Set(prev);
					newSet.delete(locationValue);
					return newSet;
				});
				
				return geocodeLocation(locationValue, retryCount + 1);
			}
			
			console.error(`Geocoding error for location ${locationValue}:`, error);
			return null;
		} finally {
			setGeocodingInProgress(prev => {
				const newSet = new Set(prev);
				newSet.delete(locationValue);
				return newSet;
			});
		}
	};

	// Process data for map view when viewMode is 'map'
	useEffect(() => {
		if (viewMode !== 'map') {
			return;
		}


		// Check if data is empty or initial state
		if (!queryData?.pagedata || queryData.pagedata.length === 0) {
			return;
		}

		const processData = async () => {
			setIsGeocodingComplete(false);
			
			// Collect all unique location values and their corresponding data items
			const locationMap = new Map<string, any[]>();
			
			for (const dataLine of data) {
				const locationValue = (dataLine as any)[mapLocationField];
				const hasValidLocation = locationValue && locationValue !== 'Ikke oppgitt';
				
				if (hasValidLocation) {
					if (!locationMap.has(locationValue)) {
						locationMap.set(locationValue, []);
					}
					locationMap.get(locationValue)!.push(dataLine);
				}
			}
			
			// Fetch coordinates in batches to avoid overwhelming the server
			const locationValues = Array.from(locationMap.keys());
			const batchSize = 5; // Process 5 locations at a time
			const allCoordinates: (([number, number] | null))[] = [];
			
			for (let i = 0; i < locationValues.length; i += batchSize) {
				const batch = locationValues.slice(i, i + batchSize);
				const batchPromises = batch.map(location => geocodeLocation(location));
				const batchResults = await Promise.all(batchPromises);
				allCoordinates.push(...batchResults);
				
				// Update map with partial results after each batch
				const validData: any[] = [];
				const grouped: { [key: string]: any[] } = {};
				
				allCoordinates.forEach((coordinates, index) => {
					if (coordinates) {
						const locationValue = locationValues[index];
						const items = locationMap.get(locationValue)!;
						validData.push(...items);
						grouped[locationValue] = items;
					}
				});
				
				setDataWithLocation(validData);
				setDataByLocation(grouped);
				
				// Add delay between batches to respect rate limits (Nominatim: 1 req/sec)
				if (i + batchSize < locationValues.length) {
					await new Promise(resolve => setTimeout(resolve, 1000));
				}
			}
			
			setIsGeocodingComplete(true);
		};

		processData();
	}, [viewMode, queryData, mapLocationField]);

    const setPageSizeChanged = (value: any) => {
        let newPageSize = parseInt(value);
        setPageSize(value);
        setSelectedOption(value);
        setCurrentPage(0);
    };

    const handleSearch = (value: any, label: any, exact: boolean = false) => {
        let searchValues = [...searchValueArray];
        let searchValue = searchValues.find((value: any) => value["field"] == label);
        if (searchValue != undefined) {
            (searchValue["values"] as any)[0] = value;
						searchValue["exact"] = exact;
        }

        setSearchValueArray(searchValues);
				setCurrentPage(0);

		if (props.saveFiltersKey != undefined) {
			localStorage.setItem(props.saveFiltersKey, JSON.stringify(searchValues));
		}
     };

     const handleSorting = (label: any, field: any) =>{
		let direction = sortDirection === "asc" ? "desc" : "asc";
		label = label.split("|")[0];
		if (label != selectedHeaderField) {
			direction = field.defaultSortDirection != undefined ? field.defaultSortDirection : "desc";
		}
        setSortDirection(direction);
        setSelectedHeaderField(label);
     }
    
	function setPagination(pageData: any) {
		const { pagestotal, totalcount } = pageData;
		const next = () => setCurrentPage(Math.min(currentPageNumber + 1, pagestotal - 1));
		const prev = () => setCurrentPage(Math.max(currentPageNumber - 1, 0));

		const buildPages = (): (number | '...')[] => {
			if (pagestotal <= 7) {
				return Array.from({ length: pagestotal }, (_, i) => i);
			}
			const around = Array.from(new Set(
				[0, pagestotal - 1, currentPageNumber, currentPageNumber - 1, currentPageNumber + 1]
					.filter(n => n >= 0 && n < pagestotal)
			)).sort((a, b) => a - b);
			const result: (number | '...')[] = [];
			let prev = -1;
			for (const p of around) {
				if (prev !== -1 && p - prev > 1) result.push('...');
				result.push(p);
				prev = p;
			}
			return result;
		};

		const isAtStart = currentPageNumber === 0;
		const isAtEnd = currentPageNumber + 1 === pagestotal;

		return (
			<div className={styles.pagination}>
				<div className={styles.paginationInner}>
					<button
						className={`${styles.paginationNavBtn} ${isAtStart ? styles.disabled : ''}`}
						onClick={prev}
						disabled={isAtStart}
					>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
						</svg>
						{t("general.table.previous")}
					</button>

					<div className={styles.paginationPages}>
						{buildPages().map((p, i) =>
							p === '...'
								? <span key={`ellipsis-${i}`} className={styles.paginationEllipsis}>...</span>
								: <button
									key={p}
									className={`${styles.paginationPageBtn} ${p === currentPageNumber ? styles.paginationPageActive : ''}`}
									onClick={() => setCurrentPage(p as number)}
								>
									{(p as number) + 1}
								</button>
						)}
					</div>

					<button
						className={`${styles.paginationNavBtn} ${isAtEnd ? styles.disabled : ''}`}
						onClick={next}
						disabled={isAtEnd}
					>
						{t("general.table.next")}
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
						</svg>
					</button>
				</div>
				<div className={styles.paginationMeta}>
					<span className={styles.totalcount}>{t("general.table.total") || "Totalt"}: {totalcount || 0}</span>
					<select
						onChange={e => setPageSizeChanged(e.target.value)}
						value={selectedOption}
					>
						{showItemsRange.map(size => <option key={size} value={size}>{size}</option>)}
					</select>
				</div>
			</div>
		);
	}

	const getFieldValue = (dataLine: any, field: any) => {
		const value = dataLine[field.field];
		
		if (value === null || value === undefined) {
			return 'Ikke oppgitt';
		}
		
		// Handle dates
		if (field.field.includes('DATE') || field.field.includes('TIMESTAMP')) {
			try {
				const date = new Date(value);
				if (!isNaN(date.getTime())) {
					return date.toLocaleDateString('no-NO');
				}
			} catch {
				return value;
			}
		}
		
		return value;
	};

	const renderCardView = () => {
		if (!data || data.length === 0) {
			return (
				<div className={styles.noResults}>
					<p>{t("general.table.nodata")}</p>
				</div>
			);
		}

		return (
			<div className={styles.cardsContainer}>
				{data.map((dataLine: any, index: any) => {
					const visibleFields = showLabels.filter((field: any) => field != undefined);
					
					return (
						<div
							key={rowKeyFor(dataLine, index)}
							className={styles.card}
							onClick={() => props.onLineClick?.(dataLine)}
							style={{ cursor: props.onLineClick ? 'pointer' : 'default' }}
						>
							<div className={styles.cardHeader}>
								{visibleFields.slice(0, 2).map((field: any, fieldIndex: number) => (
									<div key={fieldIndex} className={styles.cardHeaderItem}>
										<span className={styles.cardHeaderLabel}>
											{field.label || field.field}
										</span>
										<span className={styles.cardHeaderValue}>
											{getFieldValue(dataLine, field)}
										</span>
									</div>
								))}
							</div>
							
							<div className={styles.cardContent}>
								{visibleFields.slice(2).map((field: any, fieldIndex: number) => {
									const value = getFieldValue(dataLine, field);
									if (!value || value === 'Ikke oppgitt') return null;
									
									return (
										<div key={fieldIndex} className={styles.cardField}>
											<span className={styles.cardFieldLabel}>
												{field.label || field.field}:
											</span>
											<span className={styles.cardFieldValue}>
												{value}
											</span>
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	const renderMapView = () => {
		if (loading || softLoading) {
			return (
				<div className={styles.mapContainer}>
					<div className={styles.loadingMessage}>
						{t("general.table.loading")}
					</div>
				</div>
			);
		}

		const defaultCenter: [number, number] = [60.4720, 8.4689];
		const defaultZoom = 6;

		const getPopupState = (locationValue: string, totalItems: number) => {
			const state = popupStates[locationValue];
			const totalPages = Math.ceil(totalItems / 1);
			return state || { currentPage: 0, totalPages };
		};

		const updatePopupState = (locationValue: string, newState: { currentPage: number; totalPages: number }) => {
			setPopupStates(prev => ({
				...prev,
				[locationValue]: newState
			}));
		};

		const navigatePopup = (locationValue: string, direction: 'prev' | 'next') => {
			const currentState = getPopupState(locationValue, dataByLocation[locationValue]?.length || 0);
			const newPage = direction === 'next' 
				? Math.min(currentState.currentPage + 1, currentState.totalPages - 1)
				: Math.max(currentState.currentPage - 1, 0);
			
			updatePopupState(locationValue, { ...currentState, currentPage: newPage });
		};

		return (
			<div className={styles.mapContainer}>
				{!isGeocodingComplete && (
					<div className={styles.loadingBanner}>
						Laster inn lokasjoner... ({Object.keys(dataByLocation).length} funnet)
					</div>
				)}
				<MapContainer
					center={defaultCenter}
					zoom={defaultZoom}
					className={styles.map}
					ref={mapRef}
				>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					
					{Object.entries(dataByLocation).map(([locationValue, locationData]) => {
						const coordinates = geocodingCache[locationValue];
						if (!coordinates || locationData.length === 0) return null;

						const popupState = getPopupState(locationValue, locationData.length);
						const currentItem = locationData[popupState.currentPage];
						const hasMultipleItems = locationData.length > 1;
						const visibleFields = showLabels.filter((field: any) => field != undefined);

						return (
							<Marker key={locationValue} position={coordinates}>
								<Popup maxWidth={320} maxHeight={500} className={styles.customPopup}>
									<div>
										{hasMultipleItems && (
											<div className={styles.paginationControls}>
												<div className={styles.paginationInfo}>
													{popupState.currentPage + 1} {t("general.table.of")} {locationData.length}
												</div>
												<div className={styles.paginationButtons}>
													<button 
														onClick={() => navigatePopup(locationValue, 'prev')}
														disabled={popupState.currentPage === 0}
														className={styles.paginationButton}
													>
														← {t("general.table.previous")}
													</button>
													<button 
														onClick={() => navigatePopup(locationValue, 'next')}
														disabled={popupState.currentPage === popupState.totalPages - 1}
														className={styles.paginationButton}
													>
														{t("general.table.next")} →
													</button>
												</div>
											</div>
										)}
										<div className={styles.mapPopupCard}>
											<div className={styles.cardHeader}>
												{visibleFields.slice(0, 2).map((field: any, fieldIndex: number) => (
													<div key={fieldIndex} className={styles.cardHeaderItem}>
														<span className={styles.cardHeaderLabel}>
															{field.label || field.field}
														</span>
														<span className={styles.cardHeaderValue}>
															{getFieldValue(currentItem, field)}
														</span>
													</div>
												))}
											</div>
											<div className={styles.cardContent}>
												{visibleFields.slice(2).map((field: any, fieldIndex: number) => {
													const value = getFieldValue(currentItem, field);
													if (!value || value === 'Ikke oppgitt') return null;
													
													return (
														<div key={fieldIndex} className={styles.cardField}>
															<span className={styles.cardFieldLabel}>
																{field.label || field.field}:
															</span>
															<span className={styles.cardFieldValue}>
																{value}
															</span>
														</div>
													);
												})}
											</div>
											{props.onLineClick && (
												<div className={styles.cardActions}>
													<button
														className={styles.selectButton}
														onClick={() => props.onLineClick(currentItem)}
													>
														{t("general.table.select") || "Velg"}
													</button>
												</div>
											)}
										</div>
									</div>
								</Popup>
							</Marker>
						);
					})}
				</MapContainer>
			</div>
		);
	};

	// Trigger callbacks when data changes
	useEffect(() => {
		if (isFetching) {
			props.onDataFetchStarted?.();
		}
	}, [isFetching]);

	useEffect(() => {
		if (queryData && !isFetching) {
			props.onDataFetched?.(queryData);
		}
	}, [queryData, isFetching]);

	function exportToExcel() {

		let selectedCols = [
			...(props.permaFilters != undefined ? props.permaFilters.map((filter: any) => filter.field) : []),
			...(props.selectedTraits != undefined ? props.selectedTraits : []),
			...(showLabels != undefined ? showLabels.map((label: any) => label?.field?.split("|")[0]) : []),
			props.showOnlyBreedingdogs == true ? "BREEDING_ALLOWED" : "",
			props.showOnlyApproveddogs == true ? "APPROVEDDOGS_ONLY" : "",
			props.includeDogsOnWait == true ? "DOGS_ON_WAIT_ONLY" : "",
			"EVERYTHING_EXPORT"
		].filter((value: any) => value != "");

		let filters = [
			...(props.permaFilters != undefined ? props.permaFilters : []),
			...searchValueArray.filter((searchValue: any) => searchValue['values'].length != 0),
		];

		axios.single({
			method: 'post',
			url: dataUrl,
			responseType: 'json',
			data: {
				...(props.data != undefined ? props.data : {}),
				pagenumber: currentPageNumber,
				pagesize: pageSize,
				//sorting: { field: selectedHeaderField, order: sortDirection }, Testing line under to see if it works
				sorting: { field: selectedCols.includes(selectedHeaderField) ? selectedHeaderField : undefined, order: selectedCols.includes(selectedHeaderField) ? sortDirection : undefined },
				exclusiveFilters: props.exclusiveFilters,
				filters: filters.filter((filter: any) => selectedCols.includes(filter.field)),
				additionalData: props.additionalData,
				selectedCols: selectedCols,
			},
			headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
		})
		.then((res) => {
			let dogsdata = res.data?.pagedata;

			// Create excel file from dogsdata
			const workbook = new ExcelJS.Workbook();
			const worksheet = workbook.addWorksheet('Dogs Data');

			// Add headers
			worksheet.addRow(Object.keys(dogsdata[0]));

			// Add data rows
			dogsdata.forEach((dog: any) => {
				worksheet.addRow(Object.values(dog));
			});

			// Generate buffer
			workbook.xlsx.writeBuffer().then((buffer: any) => {
				// Trigger download
				const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = 'dogsdata.xlsx';
				link.click();
			});
			

		}).catch((err) => {
			props?.onFetchError?.(err);
		});
	}

	useEffect(() => {
		if(
			(updateData != props.updateTable || JSON.stringify(permaFiltersPrevious) != JSON.stringify(props.permaFilters))
		){
			// Første kjøring skjer rett etter montering, der permaFiltersPrevious
			// fortsatt er null og sidetallet nettopp er lest fra localStorage. Å
			// nullstille her ville gjort lagringen virkningsløs — man ville alltid
			// landet på side 1. Senere kjøringer er ekte endringer (f.eks. bytte av
			// fane), og da skal sidetallet nullstilles.
			if (!isFirstPermaFilterRun.current) {
				setCurrentPage(0);
			}
			refetch();
		}
		isFirstPermaFilterRun.current = false;
		setUpdateData(props.updateTable);
		setPermaFiltersPrevious(props.permaFilters);
		setSelectedOption(pageSize);
    }, [
		props.dataUrl,
		props.updateTable,
		props.exclusiveFilters,
		props.permaFilters,
	]);

	useEffect(() => {
		if (props.resetFilters > 0) {
			// Reset searchValueArray to empty values
			let newValue = JSON.parse(JSON.stringify(props.options.showFields.map((label: any) => {
				return { field: label.field, type: "and", values: JSON.parse(JSON.stringify([''])), exact: false };
			})));
			setSearchValueArray([...newValue]);

			// Clear localStorage if saveFiltersKey is set
			if (props.saveFiltersKey != undefined) {
				localStorage.setItem(props.saveFiltersKey, JSON.stringify(newValue));
			}

			setCurrentPage(0);
			setFilterInputGeneration((generation: number) => generation + 1);

			// Clear value of all input fields
			let inputs = document.getElementsByTagName("input");
			for (let i = 0; i < inputs.length; i++) {
				inputs[i].value = "";
			}
		}
	}, [props.resetFilters]);

	// props.options.showFields er et nytt array ved hver render av forelderen, så
	// effekten under kan ikke ha det som dependency direkte — den ville kjørt hver
	// gang forelderen rendret av en helt urelatert grunn og skrevet over filtrene
	// brukeren nettopp satte. Feltnavnene er det effekten faktisk bryr seg om.
	const showFieldsSignature = JSON.stringify(
		(props.options.showFields ?? []).map((label: any) => label?.field)
	);

	// Bytte av lagringsnøkkel betyr ny rase eller ny side: hent sidetallet som
	// hører til den nye nøkkelen framfor å bli stående på det forrige.
	const isFirstSaveKeyRun = useRef(true);
	useEffect(() => {
		if (isFirstSaveKeyRun.current) {
			isFirstSaveKeyRun.current = false;
			return;
		}
		setCurrentPageState(readSavedPage(savedPageKey));
		setFilterInputGeneration((generation: number) => generation + 1);
	}, [savedPageKey]);

	// Lagret sidetall kan peke forbi siste side hvis datagrunnlaget har krympet
	// siden sist. Klem det inn i stedet for å vise en tom tabell.
	useEffect(() => {
		const pagestotal = queryData?.pagestotal;
		if (pagestotal != undefined && pagestotal > 0 && currentPageNumber > pagestotal - 1) {
			setCurrentPage(pagestotal - 1);
		}
	}, [queryData?.pagestotal]);

	useEffect(() => {
		if (props.saveFiltersKey != undefined && localStorage.getItem(props.saveFiltersKey) != null) {
			let savedFilters = JSON.parse(localStorage.getItem(props.saveFiltersKey) || "{}");
			if (Array.isArray(savedFilters)) {
				// map saved filters into existing searchValueArray structure
				let newSavedFilters = props.options.showFields.map((label: any) => {
					// map existing values to new array
					let existingValue = savedFilters.find((value: any) => value?.["field"] == label?.field);
					return existingValue != undefined ? existingValue : { field: label?.field, type: "and", values: [''], exact: false };
				});

				setSearchValueArray(newSavedFilters);
			}
		} else {
			setSearchValueArray(props.filters != undefined ? props.filters : props?.options?.showFields?.map((label: any) => {
				// map existing values to new array
				let existingValue = searchValueArray.find((value: any) => value?.["field"] == label?.field);
				return existingValue != undefined ? existingValue : { field: label?.field, type: "and", values: [''], exact: false };
				//return { field: label.field, type: "and", values: [''], exact: false };
			}));
		}
	}, [showFieldsSignature, props.saveFiltersKey]);

	return (
		<div className={ styles.pagetable }>
			<Accordion>
				{viewMode === 'list' ? (
				<div
					className={styles.tablecontainer}
				>
					<Table className={`${styles.table}`} striped hover={props.hover} style={softLoading || props.loading ? { pointerEvents: "none", opacity: 0.4 } : {}}>
						<thead>
							<tr>
								{ props.dataBefore != undefined ?
									<th></th>
								: null}
								{showLabels != undefined ?
									showLabels.filter((i: any) => i != undefined).map((i: any, key: any) => <th key={key} onClick={() => {
										handleSorting(i.field, i);
									}}>{i.label} { i.field == selectedHeaderField ?
										sortDirection == "asc" ?
											<i className="fa fa-sort-alpha-down"></i>
										:
											<i className="fa fa-sort-alpha-up-alt"></i>
									: ""}</th>)
								: <th />}
								{props.dataAfter != undefined ?
									<th></th>
								: null}
							</tr>
							<tr className={styles.headerfilter}>
								{props.selectedFunctions != undefined ?
									<td className={styles.headerfilter}></td>
								: null}
								{props.dataBefore != undefined && props.dataBefore.length > 0 ? (
									<td>
										<div></div>
									</td>
									) : null
								}
								{ showLabels != undefined && searchValueArray.length > 0 ? (
									showLabels.filter((i: any) => i != undefined).map((i: any, key: any) => {
										return (
											<td key={key}>
												<div>
													{i.noFilter != true ? (i.customFilters == undefined ?
														<input
															key={`${i.field}-${filterInputGeneration}`}
															type="text"
															id={i.label}
															placeholder={t("general.table.label_filter")}
															//onChange={(event) => handleSearch(event.target.value, i.field)}
															defaultValue={
																searchValueArray.find((value: any) => value["field"] == i.field)?.values[0] || ""
															}
															onKeyDown={e => {
																if (e.key === "Enter") {
																	if (i.searchoverride != undefined) {
																		handleSearch(i.searchoverride((e.target as HTMLInputElement).value), i.field);
																		return;
																	}
																	handleSearch((e.target as HTMLInputElement).value, i.field);
																}
															}}
															onBlur={e => {
																// searchoverride rewrites the typed value before it is
																// stored (age -> birth year), so compare the overridden
																// value, not the raw input.
																const typed = (e.target as HTMLInputElement).value;
																const next = i.searchoverride != undefined ? i.searchoverride(typed) : typed;
																const current = searchValueArray.find((value: any) => value["field"] == i.field)?.values[0] ?? "";
																// Blur fires on every focus change, including straight
																// after Enter already ran the search. Only refetch when
																// the filter actually changed.
																if (`${next}` === `${current}`) return;
																handleSearch(next, i.field);
															}}
														/>
													:
														<Field
															type="select"
															disabled={ i.disableFilter != undefined }
															value={{value: searchValueArray.find((value: any) => value["field"] == i.field)?.values[0]}}
															onChange={ (val: any) => handleSearch(val.value, i.field, true) }
															options={{
																choosetext: props.filterChoosetext != undefined ? props.filterChoosetext : t("general.table.label_filter_change"),
																choices: i.customFilters
															}}
														></Field>)
													: <div/>}
												</div>
											</td>
										);
									})
								) : <td></td>}
								{props.dataAfter != undefined ?
									<td className={`${styles.headerfilter}`}></td>
								: null}
							</tr>
						</thead>
						<tbody>
							{loading ?
								Array.from({ length: pageSize }).map((_, key) => (
									<tr key={key}>
										{props.dataBefore != undefined && <td><div className={styles.skeletonCell} /></td>}
										{showLabels.filter((i: any) => i != undefined).map((_: any, labelkey: any) => (
											<td key={labelkey}><div className={styles.skeletonCell} /></td>
										))}
										{props.dataAfter != undefined && <td><div className={styles.skeletonCell} /></td>}
									</tr>
								))
							:
								data?.map((dataLine: any, key: any) =>(

									<tr className={
										props.hoverExempt != undefined ?
											props.hoverExempt(dataLine) == true ?
												`${styles.nohover}`
											: ""
										: ""
									} key={rowKeyFor(dataLine, key)} onClick={(evt) =>{
										evt.stopPropagation();
										evt.nativeEvent.stopImmediatePropagation();
										if (props.onLineClick != undefined) {
											props.onLineClick(dataLine);
										}
									}}>
										{props.dataBefore != undefined ?
											<td>
												{props.dataBefore(dataLine)}
											</td>
										: null}

										{showLabels.filter((i: any) => i != undefined).map((label: any, labelkey: any) => (
											<td width={label.width}
												key={labelkey}
												onMouseOver={(evt) => {
													if (label.maxLength != undefined && evt.currentTarget.scrollWidth != evt.currentTarget.clientWidth) {
														evt.currentTarget.classList.add("maxlengthhovering");
													}
												}}
												onMouseOut={(evt) => evt.currentTarget.classList.remove("maxlengthhovering")}
												style={label.maxLength != undefined ? {
													marginBottom: "0px",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													maxWidth: label.maxLength + "px"
												} : {}}
											>
												{label.override == undefined ?
													<p style={{marginBottom: "0px"}}>{dataLine[label.field?.split("|")[0]]}</p>
												: label.override(dataLine)}
											</td>
										))}

										{props.dataAfter != undefined ?
											<td>
												{props.dataAfter(dataLine)}
											</td>
										: null}
									</tr>))
							}
						</tbody>
					</Table>
				</div>
				) : viewMode === 'card' ? (
					<div className={styles.cardViewContainer} style={softLoading || props.loading ? { pointerEvents: "none", opacity: 0.4 } : {}}>
						{renderCardView()}
					</div>
				) : (
					<div className={styles.mapViewContainer}>
						{renderMapView()}
					</div>
				)}

				{ (props.hidePagination == undefined || props.hidePagination == true || viewMode === 'map') ?
					"" : <div className={`${styles.noPrint}`} style={(loading || softLoading || props.loading) ? { pointerEvents: "none", opacity: 0.4 } : {}}> {setPagination(loading ? { pagestotal: 1, totalcount: 0 } : pageData)} </div>
				}
				{props.hideExportExcel == true ? "" :
					<div>
						{ general.getBreedAccess(auth.user, auth.selectedBreed) == "admin" ||
						general.getBreedAccess(auth.user, auth.selectedBreed) == "user" ?
							<div
								className={styles.exportbtn}
								onClick={() => {
									exportToExcel();
								}}
							>
								Eksporter til Excel
							</div>
						: ""}
					</div>
				}
			</Accordion>
		</div>
	);
}

export default PageTable;
